// Одит на действията (`/audit-log`) — служебният екран, на който се проверява
// КОГА, КЪДЕ и КАКВО се е случило.
//
// Екранът е три пласта, отгоре надолу: обзор на периода (шест числа + лента с
// активността), разбивка „къде" и „кой" (клик по ред стеснява списъка), и
// самият списък върху споделената таблица в СЪРВЪРЕН режим. Клик по маса или
// сметка влиза в „проследяване" — филтърът се стеснява до нея и периодът се
// изключва, защото един живот на сметка минава през полунощ.
//
// Правилата и капаните на модула са в docs/audit-log.md.

import { useCallback, useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import { useAppSelector } from "../../../store/hooks";
import { selectIsAdminUser } from "../../../store/authSlice";
import { useViewport } from "../../../hooks/useViewport";
import {
  DataTable,
  readPersistedPageSize,
  readPersistedSorting,
} from "../../../components/dataTable";
import type {
  ColumnFilterRule,
  SortRule,
} from "../../../components/dataTable";
import {
  useAuditLogFilters,
  useAuditLogStats,
  useAuditLogs,
} from "../../../hooks/useAuditLogs";
import type {
  AuditLogEntry,
  AuditLogImpact,
  AuditLogListParams,
  AuditLogModule,
  AuditLogStatusClass,
} from "../../../api/auditLogs";
import AuditLogDetailModal from "./AuditLogDetailModal";
import ExportExcelButton from "../../reports/components/ExportExcelButton";
import { AuditBreakdown } from "./list/AuditBreakdown";
import { AuditFocusBar, type AuditFocus } from "./list/AuditFocusBar";
import { AuditMobileCard } from "./list/AuditMobileCard";
import { AuditOverview } from "./list/AuditOverview";
import { AuditTopBar } from "./list/AuditTopBar";
import { AuditToolbar, type QuickState, type QuickToggle } from "./list/AuditToolbar";
import { AUDIT_SORTABLE_IDS, useAuditColumns } from "./list/auditColumns";
import { exportAuditLogs, fetchAuditLogsForExport } from "./list/auditExport";
import { SLOW_MS, WRITE_METHODS } from "./list/auditTaxonomy";

const SEARCH_DEBOUNCE_MS = 350;
/** Списъкът и проследяването пазят СВОИ подредби на колоните и сортиране. */
const LIST_TABLE_ID = "audit-log";
const TRACE_TABLE_ID = "audit-log-trace";
const LIST_SORTING: SortRule[] = [{ id: "timestamp", desc: true }];
/** Следата се чете отпред назад — така се вижда какво е довело до какво. */
const TRACE_SORTING: SortRule[] = [{ id: "timestamp", desc: false }];

const QUICK_OFF: QuickState = {
  problems: false,
  money: false,
  settings: false,
  slow: false,
  reads: false,
  live: false,
};

const asList = (rules: ColumnFilterRule[], id: string): string[] => {
  const value = rules.find((rule) => rule.id === id)?.value;
  return Array.isArray(value) ? value.map(String) : [];
};

const asRange = (
  rules: ColumnFilterRule[],
  id: string,
): [number | null, number | null] => {
  const value = rules.find((rule) => rule.id === id)?.value;
  if (!Array.isArray(value)) return [null, null];
  const [min, max] = value as [number | null, number | null];
  return [
    typeof min === "number" && Number.isFinite(min) ? min : null,
    typeof max === "number" && Number.isFinite(max) ? max : null,
  ];
};

export default function AuditLog() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isNarrow } = useViewport();

  const authStatus = useAppSelector((state) => state.auth.status);
  const isAdmin = useAppSelector(selectIsAdminUser);

  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(() => [dayjs().startOf("day"), dayjs().endOf("day")]);
  const [focus, setFocus] = useState<AuditFocus | null>(null);
  const [quick, setQuick] = useState<QuickState>(QUICK_OFF);
  const [moduleSelection, setModuleSelection] = useState<AuditLogModule[]>([]);
  const [userSelection, setUserSelection] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFilterRule[]>([]);
  const [sorting, setSorting] = useState<SortRule[]>(() =>
    readPersistedSorting(LIST_TABLE_ID, LIST_SORTING, AUDIT_SORTABLE_IDS),
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(() =>
    readPersistedPageSize(LIST_TABLE_ID, 50),
  );
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const tableId = focus ? TRACE_TABLE_ID : LIST_TABLE_ID;

  // Влизането и излизането от проследяване сменя таблицата (тя се пресъздава
  // с `key`), затова тук се взима подредбата НА ТАЗИ таблица: разминат ли се,
  // стрелката в хедъра сочи едно, а заявката пита за друго.
  useEffect(() => {
    const fallback = tableId === TRACE_TABLE_ID ? TRACE_SORTING : LIST_SORTING;
    setSorting(readPersistedSorting(tableId, fallback, AUDIT_SORTABLE_IDS));
    setPageSize(readPersistedPageSize(tableId, 50));
    setColumnFilters([]);
    setPageIndex(0);
  }, [tableId]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchText]);

  // Филтрите БЕЗ страниране и подредба — списъкът, обобщението и опциите на
  // менютата трябва да гледат един и същ набор.
  const baseFilters = useMemo<AuditLogListParams>(() => {
    const params: AuditLogListParams = {};

    if (debouncedSearch) params.search = debouncedSearch;

    const operations = asList(columnFilters, "operation");
    if (operations.length) params.operation = operations;

    const methods = asList(columnFilters, "method");
    if (methods.length) {
      params.method = methods;
    } else if (!quick.reads) {
      // Превключвателят е долу → само мутации. Изричният избор в колоната бие
      // подразбирането, иначе човек маха отметката и нищо не се променя.
      params.method = [...WRITE_METHODS];
    }

    const statusClasses = asList(columnFilters, "status_code");
    if (statusClasses.length) {
      params.status_class = statusClasses as AuditLogStatusClass[];
    }

    const impacts: AuditLogImpact[] = [];
    if (quick.money) impacts.push("money");
    if (quick.settings) impacts.push("settings");
    if (impacts.length) params.impact = impacts;

    if (moduleSelection.length) params.module = moduleSelection;
    if (userSelection.length) params.user_id = userSelection;
    if (quick.problems) params.only_problems = true;

    const [durationMin, durationMax] = asRange(columnFilters, "duration_ms");
    const minDuration = quick.slow
      ? Math.max(SLOW_MS, durationMin ?? 0)
      : durationMin;
    if (minDuration !== null) params.min_duration_ms = minDuration;
    if (durationMax !== null) params.max_duration_ms = durationMax;

    // Проследяването изключва периода — една сметка живее през полунощ.
    if (focus?.kind === "bill") {
      params.bill_id = focus.billId;
    } else if (focus?.kind === "table") {
      params.table_name = focus.tableName;
    } else {
      if (dateRange?.[0]) {
        params.start_date = dateRange[0].format("YYYY-MM-DDTHH:mm:ss");
      }
      if (dateRange?.[1]) {
        params.end_date = dateRange[1].format("YYYY-MM-DDTHH:mm:ss");
      }
    }

    return params;
  }, [
    debouncedSearch,
    columnFilters,
    quick,
    moduleSelection,
    userSelection,
    focus,
    dateRange,
  ]);

  // Смяна на филтър връща на първа страница — иначе човек стои на страница 7
  // от набор, който вече има три реда.
  useEffect(() => {
    setPageIndex(0);
  }, [baseFilters]);

  const listParams = useMemo<AuditLogListParams>(
    () => ({
      ...baseFilters,
      page: pageIndex + 1,
      page_size: pageSize,
      sort_by: sorting[0]?.id ?? "timestamp",
      sort_order: sorting[0] ? (sorting[0].desc ? "desc" : "asc") : "desc",
    }),
    [baseFilters, pageIndex, pageSize, sorting],
  );

  // Разбивката НЕ се стеснява от собствения си избор: филтрирал ли си по един
  // човек, другите трябва да останат видими и кликаеми. Без избор двата ключа
  // съвпадат и React Query прави една заявка вместо две.
  const facetFilters = useMemo<AuditLogListParams>(() => {
    const rest: AuditLogListParams = { ...baseFilters };
    delete rest.module;
    delete rest.user_id;
    return rest;
  }, [baseFilters]);

  const filterRange = useMemo(
    () =>
      focus
        ? {}
        : {
            start_date: baseFilters.start_date,
            end_date: baseFilters.end_date,
          },
    [focus, baseFilters.start_date, baseFilters.end_date],
  );

  const { data, isLoading, isFetching, refetch } = useAuditLogs(
    listParams,
    quick.live,
  );
  const { data: stats, isLoading: statsLoading } = useAuditLogStats(
    baseFilters,
    quick.live,
  );
  const { data: facets } = useAuditLogStats(facetFilters, quick.live);
  const { data: filterOptions } = useAuditLogFilters(isAdmin, filterRange);

  const rowCount = data?.total ?? 0;

  // Влизането в следа маха всяко стеснение освен „четенията": проследяваш
  // сметка, за да видиш ЦЕЛИЯ ѝ живот, а не само проблемните ѝ операции или
  // само това, което е направил избраният човек.
  const clearNarrowing = useCallback(() => {
    setQuick((previous) => ({
      ...previous,
      problems: false,
      money: false,
      settings: false,
      slow: false,
    }));
    setModuleSelection([]);
    setUserSelection([]);
  }, []);

  const enterBillTrace = useCallback(
    (entry: AuditLogEntry) => {
      if (entry.bill_id === null) return;
      clearNarrowing();
      setFocus({
        kind: "bill",
        billId: entry.bill_id,
        tableName: entry.table_name,
      });
    },
    [clearNarrowing],
  );

  const enterTableTrace = useCallback(
    (tableName: string) => {
      clearNarrowing();
      setFocus({ kind: "table", tableName });
    },
    [clearNarrowing],
  );

  const columns = useAuditColumns({
    filterOptions,
    onTraceBill: enterBillTrace,
    onTraceTable: enterTableTrace,
  });

  const toggleQuick = (toggle: QuickToggle) =>
    setQuick((previous) => ({ ...previous, [toggle]: !previous[toggle] }));

  const toggleModule = (module: AuditLogModule) =>
    setModuleSelection((previous) =>
      previous.includes(module)
        ? previous.filter((value) => value !== module)
        : [...previous, module],
    );

  const toggleUser = (id: number) =>
    setUserSelection((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id],
    );

  const handleExport = async () => {
    try {
      const rows = await fetchAuditLogsForExport(listParams, rowCount);
      exportAuditLogs(rows, t);
    } catch {
      message.error(t("reportsPage.exportFailed"));
    }
  };

  if (authStatus === "loading") return null;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas">
      <AuditTopBar
        isNarrow={isNarrow}
        isFetching={isFetching}
        onRefresh={() => refetch()}
      />

      <main
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          isNarrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <AuditToolbar
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            dateDisabled={focus !== null}
            quick={quick}
            onToggle={toggleQuick}
          />

          {focus ? (
            <AuditFocusBar
              focus={focus}
              count={rowCount}
              onExit={() => setFocus(null)}
            />
          ) : null}

          <AuditOverview
            stats={stats}
            loading={statsLoading}
            isNarrow={isNarrow}
            activeProblems={quick.problems}
            activeMoney={quick.money}
            activeSettings={quick.settings}
            onToggleProblems={() => toggleQuick("problems")}
            onToggleMoney={() => toggleQuick("money")}
            onToggleSettings={() => toggleQuick("settings")}
            onPickPeriod={(range) => {
              setFocus(null);
              setDateRange(range);
            }}
          />

          <AuditBreakdown
            modules={facets?.by_module ?? []}
            users={facets?.by_user ?? []}
            selectedModules={moduleSelection}
            selectedUserIds={userSelection}
            onToggleModule={toggleModule}
            onToggleUser={toggleUser}
          />

          <DataTable<AuditLogEntry>
            key={tableId}
            tableId={tableId}
            data={data?.items ?? []}
            columns={columns}
            getRowId={(entry) => String(entry.id)}
            mode="server"
            server={{
              rowCount,
              pageIndex,
              onPageIndexChange: setPageIndex,
              onPageSizeChange: setPageSize,
              onSortingChange: setSorting,
              onColumnFiltersChange: setColumnFilters,
            }}
            loading={isLoading}
            refreshing={isFetching && !isLoading}
            ariaLabel={t("auditLog.title")}
            title={
              <div className="flex min-w-0 flex-col gap-1">
                <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                  {focus
                    ? t("auditLog.trace.kicker")
                    : t("auditLog.tableKicker")}
                </span>
                <span className="text-[16px] font-extrabold leading-none text-ink">
                  {rowCount === 1
                    ? t("auditLog.tableCountOne", { count: rowCount })
                    : t("auditLog.tableCount", { count: rowCount })}
                  <span className="font-medium text-muted">
                    {` · ${t("auditLog.tableHint")}`}
                  </span>
                </span>
              </div>
            }
            search={{
              value: searchText,
              onChange: setSearchText,
              placeholder: t("auditLog.searchPlaceholder"),
            }}
            toolbarRight={
              <ExportExcelButton
                onExport={handleExport}
                disabled={rowCount === 0}
                bare
              />
            }
            onRowClick={setSelectedEntry}
            rowTone={(entry) =>
              entry.status_code >= 400 || entry.fiscal_failed ? "danger" : null
            }
            emptyTitle={t("auditLog.noResults")}
            emptyHint={t("auditLog.emptyHint")}
            pageSize={pageSize}
            pageSizeOptions={[25, 50, 100, 200, 500]}
            mobileCard={(entry) => <AuditMobileCard entry={entry} />}
            initialSorting={focus ? TRACE_SORTING : LIST_SORTING}
          />
        </div>
      </main>

      <AuditLogDetailModal
        open={selectedEntry !== null}
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </div>
  );
}
